/**
 * /admin/users — Admin 页面:用户列表 + 添加 / 编辑 / 重置密码 / 软删除
 *
 * 鉴权:前端 useAuth().user.isAdmin 守卫;非 admin 直接跳走。
 * API:全部走 src/lib/api.ts 的 5 个 admin user 方法(JWT 自动带上)。
 */
import { useState, useEffect, type FormEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/store'
import {
  listAdminUsers, createAdminUser, updateAdminUser,
  resetAdminPassword, deleteAdminUser,
  ApiError, translateError,
  type AdminUser,
} from '../lib/api'

const LIMIT = 10

export default function AdminUsers() {
  const { user } = useAuth()
  const nav = useNavigate()

  const [users, setUsers] = useState<AdminUser[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [showAdd, setShowAdd] = useState(false)
  const [editing, setEditing] = useState<AdminUser | null>(null)
  const [resetting, setResetting] = useState<AdminUser | null>(null)

  useEffect(() => {
    if (!user) { nav('/login'); return }
    if (!user.isAdmin) { nav('/'); return }
  }, [user, nav])

  useEffect(() => {
    if (user?.isAdmin) refresh()
  }, [page, user])

  async function refresh() {
    setLoading(true); setErr(null); setSuccess(null)
    try {
      const r = await listAdminUsers({ page, limit: LIMIT, q: q.trim() || undefined })
      setUsers(r.users)
      setTotal(r.total)
    } catch (e) {
      setErr(e instanceof ApiError ? translateError(e.code) : String((e as Error).message))
    } finally { setLoading(false) }
  }

  async function onSearch(e: FormEvent) {
    e.preventDefault()
    setPage(1)
    await refresh()
  }

  async function onDelete(u: AdminUser) {
    if (!confirm(`确定要删除用户 ${u.phone}?\n此操作不可撤销(软删,status 设为 deleted)。`)) return
    try {
      await deleteAdminUser(u.id)
      setSuccess(`已删除 ${u.phone}`)
      await refresh()
    } catch (e) {
      setErr(e instanceof ApiError ? translateError(e.code) : String((e as Error).message))
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / LIMIT))
  if (!user?.isAdmin) return null

  return (
    <div>
      <h2 className="serif" style={{ fontSize: 'var(--fs-h2)', fontWeight: 600, margin: 0 }}>用户管理</h2>
      <p style={{ marginTop: '.25rem', fontSize: 'var(--fs-capt)', color: 'var(--ink2)' }}>
        共 {total} 个用户(已包含已删除)
      </p>

      {/* 工具栏:搜索 + 添加 */}
      <div className="card" style={{ marginTop: '1rem', padding: '1rem', display: 'flex', gap: '.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <form onSubmit={onSearch} style={{ display: 'flex', gap: '.5rem' }}>
          <input
            className="inp"
            placeholder="搜索手机号 / 昵称"
            value={q}
            onChange={e => setQ(e.target.value)}
            style={{ width: 220 }}
          />
          <button type="submit" disabled={loading} className="btn-ghost" style={{ fontSize: 'var(--fs-capt)' }}>搜索</button>
          {q && (
            <button type="button" onClick={() => { setQ(''); setPage(1); setTimeout(refresh, 0) }} className="btn-ghost" style={{ fontSize: 'var(--fs-capt)' }}>
              清空
            </button>
          )}
        </form>
        <button onClick={() => setShowAdd(true)} className="btn-ink" style={{ marginLeft: 'auto', fontSize: 'var(--fs-capt)' }}>
          + 添加用户
        </button>
      </div>

      {err && <div className="alert-err" style={{ marginTop: '.5rem' }}>{err}</div>}
      {success && <div className="alert-ok" style={{ marginTop: '.5rem' }}>{success}</div>}

      {/* 表格 */}
      <div className="card" style={{ marginTop: '1rem', padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-body)' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--rule)' }}>
              <th style={th}>ID</th>
              <th style={th}>手机号</th>
              <th style={th}>昵称</th>
              <th style={th}>角色</th>
              <th style={th}>状态</th>
              <th style={th}>创建时间</th>
              <th style={th}>最近登录</th>
              <th style={{ ...th, textAlign: 'right' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: 'var(--ink3)' }}>加载中…</td></tr>
            )}
            {!loading && users.length === 0 && (
              <tr><td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: 'var(--ink3)' }}>暂无用户</td></tr>
            )}
            {!loading && users.map(u => (
              <tr key={u.id} style={{ borderBottom: '1px solid var(--rule)' }}>
                <td style={{ ...td, fontFamily: 'var(--mono)', color: 'var(--ink3)' }}>{u.id}</td>
                <td style={{ ...td, fontFamily: 'var(--mono)' }}>{u.phone}</td>
                <td style={td}>{u.nickname || <span style={{ color: 'var(--ink3)' }}>—</span>}</td>
                <td style={td}>
                  {u.is_admin === 1
                    ? <span className="tag-admin" style={{ padding: '1px 6px', fontSize: 10, fontWeight: 600, letterSpacing: '.12em' }}>ADMIN</span>
                    : <span style={{ color: 'var(--ink3)', fontSize: 'var(--fs-capt)' }}>用户</span>}
                </td>
                <td style={td}><StatusBadge status={u.status} /></td>
                <td style={{ ...td, fontFamily: 'var(--mono)', fontSize: 'var(--fs-capt)', color: 'var(--ink2)' }}>{u.created_at.slice(0, 16)}</td>
                <td style={{ ...td, fontFamily: 'var(--mono)', fontSize: 'var(--fs-capt)', color: 'var(--ink2)' }}>{u.last_login_at ? u.last_login_at.slice(0, 16) : '—'}</td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <button onClick={() => setEditing(u)} className="btn-ghost" style={{ fontSize: 'var(--fs-capt)', padding: '.2rem .5rem' }}>编辑</button>
                  <button onClick={() => setResetting(u)} className="btn-ghost" style={{ fontSize: 'var(--fs-capt)', padding: '.2rem .5rem' }}>重置密码</button>
                  {u.id !== user.id && (
                    <button onClick={() => onDelete(u)} className="btn-ghost" style={{ fontSize: 'var(--fs-capt)', padding: '.2rem .5rem', color: 'var(--accent)' }}>删除</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 分页 */}
      {totalPages > 1 && (
        <div style={{ marginTop: '1rem', display: 'flex', gap: '.5rem', justifyContent: 'center', alignItems: 'center', fontSize: 'var(--fs-capt)' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1 || loading} className="btn-ghost">上一页</button>
          <span style={{ fontFamily: 'var(--mono)' }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading} className="btn-ghost">下一页</button>
        </div>
      )}

      {showAdd && <AddUserModal onClose={() => setShowAdd(false)} onDone={async () => { setShowAdd(false); await refresh() }} />}
      {editing && <EditUserModal user={editing} currentUserId={user.id} onClose={() => setEditing(null)} onDone={async () => { setEditing(null); await refresh() }} />}
      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} onDone={async () => { setResetting(null); await refresh() }} />}
    </div>
  )
}

const th = { padding: '.5rem .75rem', textAlign: 'left' as const, fontWeight: 600, color: 'var(--ink3)', fontSize: 'var(--fs-capt)' }
const td = { padding: '.5rem .75rem' }

function StatusBadge({ status }: { status: string }) {
  if (status === 'active') return <span style={{ color: 'var(--ok)', fontSize: 'var(--fs-capt)', fontWeight: 500 }}>正常</span>
  if (status === 'banned') return <span style={{ color: 'var(--accent)', fontSize: 'var(--fs-capt)', fontWeight: 500 }}>封禁</span>
  if (status === 'deleted') return <span style={{ color: 'var(--ink3)', fontSize: 'var(--fs-capt)' }}>已删除</span>
  return <span style={{ color: 'var(--ink3)', fontSize: 'var(--fs-capt)' }}>{status}</span>
}

// ---- 模态基础组件(全屏遮罩 + 居中卡片) ----
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
    }}>
      <div onClick={e => e.stopPropagation()} className="card" style={{
        width: 'min(440px, 92vw)', padding: '1.25rem', background: 'var(--surface)',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 className="serif" style={{ fontSize: 'var(--fs-h3)', fontWeight: 600, margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--ink3)' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function Field({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: '.75rem' }}>
      <label className="eyebrow" style={{ display: 'block', marginBottom: 4 }}>
        {label}{required && <span style={{ color: 'var(--accent)' }}> *</span>}
      </label>
      {children}
      {hint && <div style={{ fontSize: 'var(--fs-capt)', color: 'var(--ink3)', marginTop: 4 }}>{hint}</div>}
    </div>
  )
}

// ---- 添加用户 ----
function AddUserModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [nickname, setNickname] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault(); setErr(null)
    if (!/^1\d{10}$/.test(phone)) { setErr('手机号是 11 位数字,首位 1'); return }
    if (password.length < 8) { setErr('密码至少 8 位'); return }
    setBusy(true)
    try {
      await createAdminUser({ phone, password, nickname: nickname || undefined, isAdmin })
      onDone()
    } catch (e) {
      setErr(e instanceof ApiError ? translateError(e.code) : String((e as Error).message))
    } finally { setBusy(false) }
  }

  return (
    <Modal title="添加用户" onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="手机号" required>
          <input className="inp" value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, ''))} maxLength={11} placeholder="11 位手机号" />
        </Field>
        <Field label="初始密码" required hint="至少 8 位,用户可登录后自行修改">
          <input type="password" className="inp" value={password} onChange={e => setPassword(e.target.value)} minLength={8} autoComplete="new-password" />
        </Field>
        <Field label="昵称(可选)">
          <input className="inp" value={nickname} onChange={e => setNickname(e.target.value)} maxLength={40} placeholder="留空则用手机号" />
        </Field>
        <Field label="角色">
          <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} />
            <span style={{ fontSize: 'var(--fs-capt)' }}>设为管理员(可访问用户管理 + 事件看板)</span>
          </label>
        </Field>
        {err && <div className="alert-err" style={{ marginBottom: '.5rem' }}>{err}</div>}
        <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button type="button" onClick={onClose} className="btn-ghost">取消</button>
          <button type="submit" disabled={busy} className="btn-ink">{busy ? '创建中…' : '创建'}</button>
        </div>
      </form>
    </Modal>
  )
}

// ---- 编辑用户 ----
function EditUserModal({ user, currentUserId, onClose, onDone }: { user: AdminUser; currentUserId: number; onClose: () => void; onDone: () => void }) {
  const [nickname, setNickname] = useState(user.nickname || '')
  const [status, setStatus] = useState<'active' | 'banned'>(user.status === 'banned' ? 'banned' : 'active')
  const [isAdmin, setIsAdmin] = useState(user.is_admin === 1)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const isSelf = user.id === currentUserId

  async function submit(e: FormEvent) {
    e.preventDefault(); setErr(null)
    setBusy(true)
    try {
      await updateAdminUser(user.id, {
        nickname: nickname.trim() || null,
        status,
        isAdmin,
      })
      onDone()
    } catch (e) {
      setErr(e instanceof ApiError ? translateError(e.code) : String((e as Error).message))
    } finally { setBusy(false) }
  }

  return (
    <Modal title={`编辑用户 #${user.id}`} onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="手机号">
          <input className="inp" value={user.phone} disabled />
        </Field>
        <Field label="昵称" hint="留空保存会清空昵称">
          <input className="inp" value={nickname} onChange={e => setNickname(e.target.value)} maxLength={40} />
        </Field>
        <Field label="状态">
          <select className="inp" value={status} onChange={e => setStatus(e.target.value as 'active' | 'banned')}>
            <option value="active">正常</option>
            <option value="banned">封禁</option>
          </select>
        </Field>
        <Field label="角色" hint={isSelf ? '不能把自己降级为非管理员' : undefined}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', cursor: isSelf ? 'not-allowed' : 'pointer', userSelect: 'none', opacity: isSelf && isAdmin ? 1 : 1 }}>
            <input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} disabled={isSelf && isAdmin} />
            <span style={{ fontSize: 'var(--fs-capt)' }}>管理员{isSelf && isAdmin && <span style={{ color: 'var(--ink3)' }}>(自己)</span>}</span>
          </label>
        </Field>
        {err && <div className="alert-err" style={{ marginBottom: '.5rem' }}>{err}</div>}
        <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button type="button" onClick={onClose} className="btn-ghost">取消</button>
          <button type="submit" disabled={busy} className="btn-ink">{busy ? '保存中…' : '保存'}</button>
        </div>
      </form>
    </Modal>
  )
}

// ---- 重置密码 ----
function ResetPasswordModal({ user, onClose, onDone }: { user: AdminUser; onClose: () => void; onDone: () => void }) {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault(); setErr(null)
    if (password.length < 8) { setErr('密码至少 8 位'); return }
    if (password !== confirm) { setErr('两次密码不一致'); return }
    setBusy(true)
    try {
      await resetAdminPassword(user.id, password)
      onDone()
    } catch (e) {
      setErr(e instanceof ApiError ? translateError(e.code) : String((e as Error).message))
    } finally { setBusy(false) }
  }

  return (
    <Modal title={`重置密码 #${user.id}`} onClose={onClose}>
      <form onSubmit={submit}>
        <div style={{ fontSize: 'var(--fs-capt)', color: 'var(--ink2)', marginBottom: '.75rem' }}>
          正在重置 <span className="mono">{user.phone}</span> 的密码
        </div>
        <Field label="新密码" required hint="至少 8 位">
          <input type="password" className="inp" value={password} onChange={e => setPassword(e.target.value)} minLength={8} autoComplete="new-password" />
        </Field>
        <Field label="确认新密码" required>
          <input type="password" className="inp" value={confirm} onChange={e => setConfirm(e.target.value)} minLength={8} autoComplete="new-password" />
        </Field>
        {err && <div className="alert-err" style={{ marginBottom: '.5rem' }}>{err}</div>}
        <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button type="button" onClick={onClose} className="btn-ghost">取消</button>
          <button type="submit" disabled={busy} className="btn-ink">{busy ? '重置中…' : '重置'}</button>
        </div>
      </form>
    </Modal>
  )
}
